import { createRouter, createWebHistory } from 'vue-router'
import { abortAllRequests } from './api'
import AuthView from './views/AuthView.vue'
import NotFoundView from './views/NotFoundView.vue'
import { useSystemStore } from './stores/system'
import { useErrorStore } from './stores/error'
import qs from 'qs'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (from.path === to.path) {
      return
    }

    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
  parseQuery(query) {
    return qs.parse(query, {
      decoder(str, defaultDecoder, charset, type) {
        if (type === 'value') {
          // Boolean
          if (['true', 'false'].includes(str)) {
            return str === 'true'
          }

          // Numeric
          if (str && !isNaN(str)) {
            return parseFloat(str)
          }

          if (str === '') {
            return null
          }
        }

        return defaultDecoder(str)
      },
      comma: true,
      allowDots: true
    })
  },
  stringifyQuery(query) {
    const newQuery = qs.stringify(query, {
      encodeValuesOnly: true,
      arrayFormat: 'comma',
      skipNulls: true,
      allowDots: true
    })
    if (newQuery) {
      window.history.replaceState(window.history.state, '', `?${newQuery}`)
    }
    return newQuery
  },
  routes: [
    {
      path: '/oauth/login',
      name: 'unauthorized',
      component: AuthView
    },
    {
      path: '/my-account',
      name: 'my-account',
      component: () => import('@/views/MyAccountView.vue')
    },

    // START - Admin
    {
      path: '/admin',
      redirect: '/admin/pages',
      name: 'admin',
      component: () => import('@/views/AdminView.vue'),
      children: [
        {
          path: 'pages',
          name: 'admin.pages',
          component: () => import('@/views/admin/pages/AdminPageIndexView.vue')
        },
        {
          path: 'news',
          name: 'admin.news',
          component: () => import('@/views/admin/news/AdminNewsIndexView.vue')
        },
        {
          path: 'alerts',
          name: 'admin.alerts',
          component: () => import('@/views/admin/alerts/AdminAlertIndexView.vue')
        },
        {
          path: 'events',
          name: 'admin.events',
          component: () => import('@/views/admin/events/AdminEventIndexView.vue')
        },
        {
          path: 'files/:membersHubFolder?',
          name: 'admin.files',
          component: () => import('@/views/admin/files/AdminFileShareView.vue')
        }
      ]
    },
    {
      path: '/admin/pages/create',
      name: 'admin.pages.create',
      component: () => import('@/views/admin/pages/AdminPageEditView.vue')
    },
    {
      path: '/admin/pages/:membersHubPage',
      name: 'admin.pages.edit',
      component: () => import('@/views/admin/pages/AdminPageEditView.vue')
    },
    {
      path: '/admin/news/create',
      name: 'admin.news.create',
      component: () => import('@/views/admin/news/AdminNewsEditView.vue')
    },
    {
      path: '/admin/news/:membersHubArticle',
      name: 'admin.news.edit',
      component: () => import('@/views/admin/news/AdminNewsEditView.vue')
    },
    {
      path: '/admin/alerts/create',
      name: 'admin.alerts.create',
      component: () => import('@/views/admin/alerts/AdminAlertEditView.vue')
    },
    {
      path: '/admin/alerts/:membersHubAlert',
      name: 'admin.alerts.edit',
      component: () => import('@/views/admin/alerts/AdminAlertEditView.vue')
    },
    {
      path: '/admin/events/create',
      name: 'admin.events.create',
      component: () => import('@/views/admin/events/AdminEventEditView.vue')
    },
    {
      path: '/admin/events/:membersHubEvent',
      name: 'admin.events.edit',
      component: () => import('@/views/admin/events/AdminEventEditView.vue')
    },
    // END - Admin

    {
      path: '/:notfound(.*)*',
      name: 'notfound',
      component: NotFoundView
    }
  ]
})

router.beforeEach(async (to, from /*next*/) => {
  if (from.name === to.name) {
    return
  }

  const systemStore = useSystemStore()
  const hubName = systemStore.hub?.name || 'Members Hub'

  for (let i = 0; i < systemStore.confirmExitMethods.length; i++) {
    if (typeof systemStore.confirmExitMethods[i] === 'function' && !systemStore.confirmExitMethods[i](to)) {
      return false
    }
  }

  document.title = to.meta?.name
    ? `${to.meta.name.replace(/{{([A-z]+)}}/g, (m, p1) => to.params[p1] || '')} - ${hubName}`
    : hubName

  systemStore.overridePageTitle()

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: to.fullPath,
      page_title: document.title
    })
  }

  systemStore.setPreviousRoute({
    id: from?.meta.id,
    name: from?.name,
    path: router.resolve({
      name: from?.name,
      params: from ? { ...from.params } : null,
      query: from ? { ...from.query } : null
    })?.fullPath
  })
  await abortAllRequests()
})

router.afterEach(() => {
  const errorStore = useErrorStore()
  errorStore.clearHttpError()
})

export default router
